/**
 * Coolman Workforce · Operational Coverage Workspace (WF-107C).
 *
 * Product Owner directive 2026-02-04 (Wave 3). Minimal but production-
 * ready workspace stub answering, in one glance:
 *
 *     1. Who is on call **right now**?
 *     2. When does coverage **change next**, and to whom?
 *     3. What does the **next 6 weeks** look like?
 *     4. What **exceptions** (replacements / swaps) have been booked?
 *
 * Read-only for Owner; Administrator can:
 *     - Replace the assigned driver on any rotation window.
 *     - Undo any exception (restores standard).
 *
 * Roster editor and Swap dialog are deferred to WF-107C v2 per PO
 * scope note ("minimal workspace stub"). Backend endpoints for both
 * are live and covered by the WF-107A/B build.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ArrowsClockwise, CalendarBlank, ClockCounterClockwise,
  User, ShieldCheck, PencilSimple, ArrowUUpLeft,
} from "@phosphor-icons/react";
import { workforceApi, formatDate } from "@/components/workforce/workforceApi";
import ActivityAvatar from "@/components/ActivityAvatar";

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const WEEKDAY_LONG = { 0: "Monday", 1: "Tuesday", 2: "Wednesday", 3: "Thursday", 4: "Friday", 5: "Saturday", 6: "Sunday" };

function friendlyDate(iso) {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
}

function longDate(iso) {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-CA", { weekday: "long", month: "long", day: "numeric" });
}

function rangeLabel(startIso, endIso) {
  if (!startIso || !endIso) return "—";
  // ``end_date`` is exclusive in the engine; render the last full day.
  const end = new Date(`${endIso}T00:00:00`);
  end.setDate(end.getDate() - 1);
  const endIsoInclusive = end.toISOString().slice(0, 10);
  return `${friendlyDate(startIso)} – ${friendlyDate(endIsoInclusive)}`;
}

function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((d - now) / (1000 * 60 * 60 * 24));
}

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
}

function activityFromException(row) {
  const map = { replacement: "Edited", swap: "Re-Sent", undo: "Cancelled" };
  return {
    action: map[row.kind] || "Edited",
    name: (row.by || "").split("@")[0] || "system",
    at: row.at,
  };
}

/* ------------------------------------------------------------------ */
/* Tab                                                                 */
/* ------------------------------------------------------------------ */

export default function CoverageTab({ readOnly, employees }) {
  const [loading, setLoading] = useState(true);
  const [roster, setRoster] = useState(null);
  const [current, setCurrent] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [replaceDialog, setReplaceDialog] = useState({ open: false, rotation: null });
  const [regenerating, setRegenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, c, up, exc] = await Promise.all([
        workforceApi.coverageRoster(),
        workforceApi.coverageCurrent(),
        workforceApi.coverageUpcoming(6),
        workforceApi.coverageExceptionHistory(10),
      ]);
      setRoster(r.data);
      setCurrent(c.data);
      setUpcoming(up.data.items || []);
      setExceptions(exc.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load coverage data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const regenerate = async () => {
    if (!window.confirm("Regenerate the standard rotation schedule for the next 52 weeks?\n\nExisting replacements and swaps will be preserved.")) return;
    setRegenerating(true);
    try {
      const { data } = await workforceApi.coverageRegenerate();
      toast.success(`Regenerated ${data.rotations_generated} rotations`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regeneration failed");
    } finally {
      setRegenerating(false);
    }
  };

  const undoException = async (rotation) => {
    if (!window.confirm(`Undo this exception and restore the standard driver${rotation.original_employee_name ? ` (${rotation.original_employee_name})` : ""}?`)) return;
    try {
      await workforceApi.coverageUndoException(rotation.id);
      toast.success("Exception undone — standard schedule restored");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Undo failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="coverage-tab">
      {/* -------- Header ---------- */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900" data-testid="coverage-title">
            Operational Coverage
          </h2>
          <p className="text-sm text-slate-500">
            {roster ? `${roster.role_name} · ${roster.sequence?.length || 0} drivers · ${roster.window_days}-day rotation, ${WEEKDAY_LONG[roster.handoff_weekday] || "?"} handoff` : "Loading rotation…"}
          </p>
        </div>
        {!readOnly && (
          <button
            type="button"
            data-testid="coverage-regenerate"
            disabled={regenerating || loading}
            onClick={regenerate}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
          >
            <ArrowsClockwise size={14} weight="bold" className={regenerating ? "animate-spin" : ""} />
            Regenerate Schedule
          </button>
        )}
      </div>

      {/* -------- Current Coverage hero ---------- */}
      <CurrentCoverageHero current={current?.current} next={current?.next} />

      {/* -------- Upcoming Rotations ---------- */}
      <section className="rounded-lg border border-slate-200 bg-white">
        <header className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <CalendarBlank size={16} weight="regular" />
            Upcoming Rotations
          </h3>
          <span className="text-xs text-slate-500">Next {upcoming.length}</span>
        </header>
        <ul className="divide-y divide-slate-100" data-testid="coverage-upcoming-list">
          {upcoming.length === 0 && (
            <li className="px-5 py-4 text-sm text-slate-500">No upcoming rotations found.</li>
          )}
          {upcoming.map((r) => (
            <li key={r.id} className="flex items-center gap-4 px-5 py-3">
              <div className="w-10 flex-shrink-0">
                <RotationDot source={r.source} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-900" data-testid={`coverage-row-name-${r.id}`}>
                  {r.employee_name}
                </div>
                <div className="text-xs text-slate-500">
                  {rangeLabel(r.start_date, r.end_date)}
                  {r.source !== "generated" && r.original_employee_name && (
                    <span className="ml-2 rounded-sm border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                      {r.source === "replacement" ? "Replacement" : "Swap"} · standard was {r.original_employee_name}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-xs text-slate-400">
                {daysUntil(r.start_date) === 0 ? "starts today" : `in ${daysUntil(r.start_date)}d`}
              </div>
              {!readOnly && (
                <div className="flex flex-shrink-0 gap-1">
                  <button
                    type="button"
                    data-testid={`coverage-replace-${r.id}`}
                    onClick={() => setReplaceDialog({ open: true, rotation: r })}
                    className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    title="Replace assigned driver for this window"
                  >
                    <PencilSimple size={12} weight="bold" />
                  </button>
                  {r.source !== "generated" && (
                    <button
                      type="button"
                      data-testid={`coverage-undo-${r.id}`}
                      onClick={() => undoException(r)}
                      className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      title="Undo exception — restore standard driver"
                    >
                      <ArrowUUpLeft size={12} weight="bold" />
                    </button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>

      {/* -------- Recent Exceptions ---------- */}
      <section className="rounded-lg border border-slate-200 bg-white">
        <header className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <ClockCounterClockwise size={16} weight="regular" />
            Recent Exceptions
          </h3>
          <span className="text-xs text-slate-500">Newest first</span>
        </header>
        <ul className="divide-y divide-slate-100" data-testid="coverage-exception-history">
          {exceptions.length === 0 && (
            <li className="px-5 py-4 text-sm text-slate-500">
              No exceptions recorded — the standard schedule is unmodified.
            </li>
          )}
          {exceptions.map((h) => (
            <li key={h.id} className="flex items-center gap-4 px-5 py-3">
              <ActivityAvatar activity={activityFromException(h)} testId={`ex-${h.id}`} size={26} />
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-900">
                  <span className="font-semibold capitalize">{h.kind}</span>
                  {" · "}
                  <span className="text-slate-700">
                    {h.from_employee_name || "—"} <span className="text-slate-400">→</span> {h.to_employee_name || "—"}
                  </span>
                </div>
                <div className="text-xs text-slate-500">
                  Window {friendlyDate(h.start_date)}
                  {h.reason && <span className="ml-2 italic text-slate-500">&quot;{h.reason}&quot;</span>}
                </div>
              </div>
              <div className="flex flex-col items-end gap-0.5 text-xs text-slate-400">
                <span>{timeAgo(h.at)}</span>
                {h.by && <span className="text-[10px]">{(h.by.split("@")[0]) || h.by}</span>}
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* -------- Roster read-only sequence ---------- */}
      <RosterCard roster={roster} />

      <ReplaceDialog
        open={replaceDialog.open}
        rotation={replaceDialog.rotation}
        employees={employees || []}
        onClose={() => setReplaceDialog({ open: false, rotation: null })}
        onSaved={async () => {
          setReplaceDialog({ open: false, rotation: null });
          await load();
        }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Sub-components                                                      */
/* ------------------------------------------------------------------ */

function CurrentCoverageHero({ current, next }) {
  return (
    <div className="grid gap-4 md:grid-cols-2" data-testid="coverage-hero">
      <HeroCard
        label="Current Coverage"
        tone="active"
        rotation={current}
        emptyText="No coverage assigned for today"
      />
      <HeroCard
        label="Next Change"
        tone="upcoming"
        rotation={next}
        emptyText="No upcoming handoff scheduled"
        showChangeIndicator
      />
    </div>
  );
}

function HeroCard({ label, tone, rotation, emptyText, showChangeIndicator }) {
  const isActive = tone === "active";
  return (
    <div
      className={`rounded-lg border p-5 shadow-sm ${
        isActive
          ? "border-slate-800 bg-charcoal text-ink-inverse"
          : "border-slate-200 bg-white"
      }`}
    >
      <div className={`text-[11px] font-semibold uppercase tracking-wider ${isActive ? "text-slate-400" : "text-slate-500"}`}>
        {label}
      </div>
      {rotation ? (
        <>
          <div className="mt-2 flex items-center gap-3">
            <div className={`flex h-11 w-11 items-center justify-center rounded-full font-semibold ${isActive ? "bg-white text-charcoal" : "bg-blue-600 text-white"}`}>
              {(rotation.employee_name || "?").split(/\s+/).map(s => s[0]).slice(0, 2).join("").toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className={`truncate text-lg font-semibold ${isActive ? "" : "text-slate-900"}`} data-testid={`coverage-hero-name-${tone}`}>
                {rotation.employee_name}
              </div>
              <div className={`text-sm ${isActive ? "text-slate-300" : "text-slate-600"}`}>
                {showChangeIndicator ? longDate(rotation.start_date) : rangeLabel(rotation.start_date, rotation.end_date)}
              </div>
            </div>
          </div>
          {rotation.source !== "generated" && (
            <div className={`mt-3 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${isActive ? "bg-amber-500/20 text-amber-200" : "border border-amber-200 bg-amber-50 text-amber-800"}`}>
              <ShieldCheck size={10} weight="bold" />
              {rotation.source === "replacement" ? "Replacement" : "Swap"} · differs from standard
            </div>
          )}
        </>
      ) : (
        <div className={`mt-3 text-sm ${isActive ? "text-slate-400" : "text-slate-500"}`}>{emptyText}</div>
      )}
    </div>
  );
}

function RotationDot({ source }) {
  const map = {
    generated:   { colour: "bg-blue-500",   title: "Standard" },
    replacement: { colour: "bg-orange-500", title: "Replacement" },
    swap:        { colour: "bg-violet-600", title: "Swap" },
  };
  const cfg = map[source] || map.generated;
  return (
    <span
      title={cfg.title}
      className={`inline-block h-2.5 w-2.5 rounded-full ${cfg.colour}`}
    />
  );
}

function RosterCard({ roster }) {
  if (!roster) return null;
  return (
    <section className="rounded-lg border border-slate-200 bg-white" data-testid="coverage-roster-card">
      <header className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <User size={16} weight="regular" />
          Rotation Sequence
        </h3>
        <span className="text-xs text-slate-500">
          Anchor {formatDate(roster.anchor_start_date)}
        </span>
      </header>
      <ol className="divide-y divide-slate-100">
        {(roster.sequence || []).map((entry) => (
          <li key={entry.employee_id} className="flex items-center gap-4 px-5 py-3">
            <div className="w-6 text-center text-xs font-semibold text-slate-500">
              {entry.position}
            </div>
            <div className="flex-1 text-sm">
              <div className="font-semibold text-slate-900">{entry.employee_name}</div>
              <div className="text-xs text-slate-500">
                {entry.employee_number ? `#${entry.employee_number} · ` : ""}
                {entry.work_group || "—"}
                {entry.status && entry.status !== "Active" && (
                  <span className="ml-2 rounded-sm border border-rose-200 bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700">
                    {entry.status}
                  </span>
                )}
              </div>
            </div>
          </li>
        ))}
      </ol>
      <div className="border-t border-slate-100 px-5 py-2 text-[11px] text-slate-400">
        Roster editing lives in WF-107C v2 (deferred). Use the API for now.
      </div>
    </section>
  );
}

function ReplaceDialog({ open, rotation, employees, onClose, onSaved }) {
  const [newEmp, setNewEmp] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const eligible = useMemo(() => {
    if (!rotation) return [];
    return employees
      .filter(e => e.status === "Active" && e.id !== rotation.employee_id)
      .sort((a, b) => a.full_name.localeCompare(b.full_name));
  }, [employees, rotation]);

  useEffect(() => {
    if (open) { setNewEmp(""); setReason(""); }
  }, [open]);

  if (!open || !rotation) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!newEmp) { toast.error("Choose a replacement driver"); return; }
    setSaving(true);
    try {
      await workforceApi.coverageReplace({
        rotation_id: rotation.id,
        new_employee_id: newEmp,
        reason: reason.trim() || null,
      });
      toast.success("Replacement recorded");
      await onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Replacement failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        data-testid="coverage-replace-dialog"
        className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-xl"
      >
        <h3 className="text-base font-semibold text-slate-900">Replace Driver</h3>
        <p className="mt-1 text-xs text-slate-500">
          {rangeLabel(rotation.start_date, rotation.end_date)} · currently <span className="font-semibold text-slate-800">{rotation.employee_name}</span>
        </p>

        <label className="mt-4 block text-xs font-semibold text-slate-700">Replacement</label>
        <select
          value={newEmp}
          onChange={(e) => setNewEmp(e.target.value)}
          data-testid="coverage-replace-select"
          className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
          required
        >
          <option value="">Choose an active employee…</option>
          {eligible.map(e => (
            <option key={e.id} value={e.id}>{e.full_name}{e.work_group ? ` · ${e.work_group}` : ""}</option>
          ))}
        </select>

        <label className="mt-4 block text-xs font-semibold text-slate-700">Reason (optional)</label>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="coverage-replace-reason"
          placeholder="e.g. Original driver on approved vacation"
          className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
        />

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            data-testid="coverage-replace-cancel"
            onClick={onClose}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="coverage-replace-submit"
            disabled={saving}
            className="rounded-md border border-slate-800 bg-charcoal px-3 py-1.5 text-sm font-semibold text-ink-inverse hover:bg-slate-900 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save Replacement"}
          </button>
        </div>
      </form>
    </div>
  );
}
